/**
 * Authentication Context Provider
 *
 * Provides auth state, login/logout actions, and permission checking
 * via React Context. No Redux dependency.
 *
 * Generated by portal_generator.py
 */

import React, { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import type { LoginCredentials, AuthState, User } from './types';
import {
  loginAction,
  logoutAction,
  refreshAction,
  restoreAuth,
  tokenStorage,
} from './authStore';

// ---------------------------------------------------------------------------
// Context Types
// ---------------------------------------------------------------------------

interface AuthContextValue extends AuthState {
  /** Resolves with the authenticated user — callers can read `.role` to drive
   *  role-aware post-login navigation without waiting for a React rerender. */
  login: (credentials: LoginCredentials, options?: { expectedAud?: 'jylaw_internal' | 'client_portal' }) => Promise<{ user: User }>;
  logout: () => Promise<void>;
  hasPermission: (permission: string) => boolean;
  hasRole: (role: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, setState] = useState<AuthState>(() => {
    // Restore session from localStorage on mount
    const user = restoreAuth();
    return {
      user,
      isAuthenticated: !!user,
      loading: false,
      error: null,
    };
  });

  // Proactive token refresh: check every 60s if token expires within 5 min
  useEffect(() => {
    if (!state.isAuthenticated) return;

    const interval = setInterval(async () => {
      if (tokenStorage.isExpiringSoon(300)) {
        try {
          const user = await refreshAction();
          setState((prev) => ({ ...prev, user }));
        } catch {
          // Refresh failed — force logout
          tokenStorage.clear();
          setState({ user: null, isAuthenticated: false, loading: false, error: null });
        }
      }
    }, 60_000);

    return () => clearInterval(interval);
  }, [state.isAuthenticated]);

  const login = useCallback(async (
    credentials: LoginCredentials,
    options?: { expectedAud?: 'jylaw_internal' | 'client_portal' },
  ): Promise<{ user: User }> => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const { user } = await loginAction(credentials, options);
      setState({ user, isAuthenticated: true, loading: false, error: null });
      return { user };
    } catch (err: any) {
      const status = err?.response?.status;
      // Surface aud/role gate errors directly (they have bilingual messages)
      const isAudError = err?.message?.includes('/client/login') || err?.message?.includes('/login');
      const message = isAudError ? err.message
        : status === 401 ? 'Invalid username or password'
        : status === 403 ? 'Account is locked or disabled'
        : status === 429 ? 'Too many attempts. Please try again later.'
        : status === 503 ? 'Service temporarily unavailable'
        : 'Authentication failed';
      setState((prev) => ({ ...prev, loading: false, error: message }));
      throw err;
    }
  }, []);

  const logout = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true }));
    await logoutAction();
    setState({ user: null, isAuthenticated: false, loading: false, error: null });
  }, []);

  const hasPermission = useCallback(
    (permission: string) => state.user?.permissions.includes(permission) ?? false,
    [state.user],
  );

  const hasRole = useCallback(
    (role: string) => state.user?.role === role,
    [state.user],
  );

  const hasAnyPermission = useCallback(
    (permissions: string[]) =>
      permissions.some((p) => state.user?.permissions.includes(p) ?? false),
    [state.user],
  );

  const hasAllPermissions = useCallback(
    (permissions: string[]) =>
      permissions.every((p) => state.user?.permissions.includes(p) ?? false),
    [state.user],
  );

  const value: AuthContextValue = {
    ...state,
    login,
    logout,
    hasPermission,
    hasRole,
    hasAnyPermission,
    hasAllPermissions,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/** Access auth state and actions. Must be used within <AuthProvider>. */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}

export default AuthProvider;
